import { FC, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEntityBasePath } from "../../../../../../utils/entityPaths";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Autocomplete,
  Alert,
  Chip
} from "@mui/material";
import { CommentDto, TradingCounterParty } from "../../../../../../api/Api";
import {
  useAssignees,
  useCounterpartyProgressOverride,
  useUpdateCounterparty
} from "../../../../dataHooks/counterpartyQueries";
import { createLifecycleActions } from "../../../../../../components/lifecycle/createLifecycleActions";
import { CommentsSection } from "../../sharedComponents/commentsSection";
import { LifecycleDialog } from "../../sharedComponents/lifecycleDialog";
import { SignOffNoteField } from "../../../../../../components/lifecycle/signOffNoteField";
import { useLifecycleComments } from "../../../../../../components/lifecycle/useLifecycleComments";
import { useRoles } from "../../../../../../auth/useRoles";
import { ownsLifecycle } from "../../../../../../auth/permissions";

type Props = {
  specificCounterparty: TradingCounterParty;
};

// ADHO-402: tech lifecycle assignees are restricted to this list of 4 emails.
// Names are resolved against the "technology" assignee group fetched from the backend.
const TECH_ASSIGNEE_EMAILS = [
  "mwa@mft-energy.com",
  "msk@mft-energy.com",
  "rkr@mft-energy.com",
  "rks@mft-energy.com",
  "tba@mft-energy.com"
];

// Suggested tech assignee per trading team, shown as a hint above the dropdowns.
const TEAM_TO_TECH_ASSIGNEE: Record<string, string> = {
  MFTAPAC1: "tba@mft-energy.com",
  MFTAPAC2: "tba@mft-energy.com",
  MFTAPAC3: "tba@mft-energy.com",
  MFTAPAC4: "tba@mft-energy.com",
  MFT1: "rks@mft-energy.com",
  MFT2: "rks@mft-energy.com",
  MFT3: "rks@mft-energy.com",
  MFT5: "rks@mft-energy.com",
  MFT4: "mwa@mft-energy.com",
  MFT6: "mwa@mft-energy.com",
  MFTUS1: "rkr@mft-energy.com",
  MFTUS2: "rkr@mft-energy.com"
};

type FormData = {
  includedInExistingTradeCapture: string;
  includedInExistingTradeCaptureAssignee: string;
  newTradeCaptureStatus: string;
  newTradeCaptureStatusAssignee: string;
  signOffNote: string;
  comments: CommentDto[];
};

export const Tech: FC<Props> = ({ specificCounterparty }) => {
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const basePath = useEntityBasePath("counterparty");
  const roles = useRoles();
  const progressOverride = useCounterpartyProgressOverride(
    specificCounterparty,
    "tech"
  );

  const allTechAssignees = useAssignees("technology");
  const techAssignees = {
    data: (allTechAssignees.data || [])
      .filter((a) => a.email && TECH_ASSIGNEE_EMAILS.includes(a.email))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
  };
  const suggestedTechAssigneeEmail = specificCounterparty.tradingTeam
    ? TEAM_TO_TECH_ASSIGNEE[specificCounterparty.tradingTeam]
    : undefined;
  const suggestedTechAssignee = suggestedTechAssigneeEmail
    ? techAssignees.data.find((a) => a.email === suggestedTechAssigneeEmail)
    : undefined;

  const [formData, setFormData] = useState<FormData>({
    includedInExistingTradeCapture:
      specificCounterparty?.techLifecycle?.includedInExistingTradeCapture ===
      true
        ? "yes"
        : specificCounterparty?.techLifecycle
              ?.includedInExistingTradeCapture === false
          ? "no"
          : "",
    includedInExistingTradeCaptureAssignee:
      specificCounterparty?.techLifecycle
        ?.includedInExistingTradeCaptureAssignee || "",
    newTradeCaptureStatus:
      specificCounterparty?.techLifecycle?.newTradeCaptureStatus || "",
    newTradeCaptureStatusAssignee:
      specificCounterparty?.techLifecycle?.newTradeCaptureStatusAssignee || "",
    signOffNote: specificCounterparty?.techLifecycle?.signOffNote || "",
    comments: specificCounterparty?.techLifecycle?.comments || []
  });

  const handleClose = () => {
    navigate(`${basePath}/${params.id}`);
  };

  const updateCounterparty = useUpdateCounterparty({
    onSuccess: handleClose
  });
  const commentUpdate = useUpdateCounterparty();

  const buildCounterparty = (
    values: FormData & { complete?: boolean }
  ): TradingCounterParty => ({
    ...specificCounterparty,
    techLifecycle: {
      // Retain completion set by Market Access when this dialog only saves or comments.
      ...specificCounterparty.techLifecycle,
      includedInExistingTradeCapture:
        values.includedInExistingTradeCapture === "yes"
          ? true
          : values.includedInExistingTradeCapture === "no"
            ? false
            : undefined,
      includedInExistingTradeCaptureAssignee:
        values.includedInExistingTradeCaptureAssignee,
      newTradeCaptureStatus: values.newTradeCaptureStatus,
      newTradeCaptureStatusAssignee: values.newTradeCaptureStatusAssignee,
      signOffNote: values.signOffNote,
      comments: values.comments,
      ...(values.complete ? { complete: true } : {})
    }
  });

  const {
    currentComment,
    setCurrentComment,
    addComment,
    deleteComment,
    isSaving
  } = useLifecycleComments({
    formData,
    setFormData,
    update: commentUpdate,
    buildEntityWithComments: (comments) =>
      buildCounterparty({ ...formData, comments })
  });

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };
      if (field === "includedInExistingTradeCapture") {
        newData.newTradeCaptureStatus = "";
        newData.newTradeCaptureStatusAssignee = "";
      }
      return newData;
    });
  };

  const { handleSave, handleComplete } = createLifecycleActions({
    formData,
    currentComment,
    currentProgress: specificCounterparty.progress?.tech,
    canSetInProgress: ownsLifecycle(roles, "tech"),
    buildEntity: (values, progress) => {
      const saved = buildCounterparty(values);
      return {
        ...saved,
        progress: { ...saved.progress, tech: progress }
      };
    },
    mutate: updateCounterparty.mutate
  });

  const open = location.pathname.includes("tech");

  const canComplete =
    formData.includedInExistingTradeCapture == "yes" ||
    (formData.includedInExistingTradeCapture && formData.newTradeCaptureStatus);

  return (
    <LifecycleDialog
      open={open}
      onClose={handleClose}
      title="Technology"
      progressOverride={progressOverride}
      maxWidth="md"
      actions={{
        onClose: handleClose,
        onSave: handleSave,
        onComplete: handleComplete,
        canComplete: !!canComplete,
        isSaving: updateCounterparty.isPending
      }}
    >
      <Box gap={2} display="flex" flexDirection="column">
        {specificCounterparty?.marketAccessLifecycle?.tradeCapture ===
          "not_needed" &&
          specificCounterparty.techLifecycle?.complete && (
            <Alert severity="info">
              Technology lifecycle was auto-completed because Market Access
              marked Trade Capture as Not needed.
            </Alert>
          )}

        <Box paddingBottom={1} display="flex" alignItems="center" gap={1}>
          <Typography variant="body1" color="text.secondary">
            Traded on
          </Typography>
          <Chip
            label={specificCounterparty.venue || "N/A"}
            variant="outlined"
            size="small"
          />
        </Box>

        {specificCounterparty?.marketAccessLifecycle?.tradeCapture !==
          "not_needed" &&
          suggestedTechAssignee && (
            <Alert severity="info">
              Suggested tech assignee for {specificCounterparty.tradingTeam}:{" "}
              {suggestedTechAssignee.name} ({suggestedTechAssignee.email})
            </Alert>
          )}

        <Box display="flex" gap={2}>
          <FormControl size="medium" required sx={{ flex: 1 }}>
            <InputLabel>Included in existing trade capture</InputLabel>
            <Select
              label="Included in existing trade capture"
              value={formData.includedInExistingTradeCapture}
              onChange={(e) =>
                handleChange("includedInExistingTradeCapture", e.target.value)
              }
            >
              <MenuItem value="yes">
                <Typography variant="body1" component="span">
                  Yes
                </Typography>
              </MenuItem>
              <MenuItem value="no">
                <Typography variant="body1" component="span">
                  No
                </Typography>
              </MenuItem>
            </Select>
          </FormControl>

          <Autocomplete
            sx={{ flex: 1 }}
            options={techAssignees.data || []}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={
              techAssignees.data?.find(
                (a) =>
                  a.email === formData.includedInExistingTradeCaptureAssignee
              ) || null
            }
            onChange={(_, newValue) =>
              handleChange(
                "includedInExistingTradeCaptureAssignee",
                newValue?.email || ""
              )
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Included in existing trade capture assignee"
                size="medium"
              />
            )}
          />
        </Box>

        {formData.includedInExistingTradeCapture === "no" && (
          <Box display="flex" gap={2}>
            <FormControl size="medium" required sx={{ flex: 1 }}>
              <InputLabel>New trade capture developed status</InputLabel>
              <Select
                label="New trade capture developed status"
                value={formData.newTradeCaptureStatus}
                onChange={(e) =>
                  handleChange("newTradeCaptureStatus", e.target.value)
                }
              >
                <MenuItem value="initiated">
                  <Typography variant="body1" component="span">
                    Initiated
                  </Typography>
                </MenuItem>
                <MenuItem value="completed">
                  <Typography variant="body1" component="span">
                    Completed
                  </Typography>
                </MenuItem>
                <MenuItem value="rejected">
                  <Typography variant="body1" component="span">
                    Rejected
                  </Typography>
                </MenuItem>
              </Select>
            </FormControl>

            <Autocomplete
              sx={{ flex: 1 }}
              options={techAssignees.data || []}
              getOptionLabel={(option) => `${option.name} (${option.email})`}
              value={
                techAssignees.data?.find(
                  (a) => a.email === formData.newTradeCaptureStatusAssignee
                ) || null
              }
              onChange={(_, newValue) =>
                handleChange(
                  "newTradeCaptureStatusAssignee",
                  newValue?.email || ""
                )
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="New trade capture assignee"
                  size="medium"
                />
              )}
            />
          </Box>
        )}

        <SignOffNoteField
          value={formData.signOffNote}
          onChange={(value) => handleChange("signOffNote", value)}
        />

        <CommentsSection
          loading={isSaving}
          comments={formData.comments}
          currentComment={currentComment}
          onCurrentCommentChange={setCurrentComment}
          saveComment={addComment}
          onDeleteComment={deleteComment}
        />
      </Box>
    </LifecycleDialog>
  );
};
